import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
// Search functionality moved to individual sections
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAuth } from '../../contexts/AuthContext';
import { useSystemPreset } from '../../contexts/SystemPresetContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { LogOut } from 'lucide-react';
import Sidebar from './Sidebar';
import { FreeTrialModal } from '../subscription';
import { doc, setDoc } from 'firebase/firestore';
import { auth, db } from '../../config/firebase';
import { SUBSCRIPTION_PLAN_IDS } from '../../types/subscription';

const DashboardLayout: React.FC = () => {
  
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  // Search functionality moved to individual sections
  const { language, setLanguage } = useLocalization();
  const { user, logout, setUser } = useAuth();
  const { getLawyerTitleName } = useSystemPreset();
  const { state: subscriptionState } = useSubscription();
  const [showFreeTrialModal, setShowFreeTrialModal] = useState(false);
  const [modalCheckCompleted, setModalCheckCompleted] = useState(false);

  // Get user's full name
  const getUserName = () => {
    if (!user) return 'User';
    const { firstName, middleName, lastName } = user;
    const nameParts = [firstName, middleName, lastName].filter(Boolean);
    return nameParts.join(' ') || 'User';
  };

  // Get user's title from their profile titleID
  const getUserTitle = () => {
    
    if (!user?.titleID) {
      return 'Legal Professional';
    }
    
    // Get the localized title name from cached preset data
    const title = getLawyerTitleName(user.titleID, language as 'en' | 'ar');
    
    // If title not found in preset data, return fallback
    if (title === 'Legal Professional') {
      return 'Legal Professional';
    }
    
    return title;
  };

  // Get welcome message with user's name
  const getWelcomeMessage = () => {
    const userName = getUserName();
    return language === 'ar' 
      ? `مرحباً بك، ${userName}!`
      : `Welcome, ${userName}!`;
  };

  // Reset modal check when subscription changes (for retry mechanism)
  useEffect(() => {
    // Reset check when subscription ID changes (new subscription loaded)
    if (subscriptionState.currentSubscription?.id && subscriptionState.currentSubscription.id !== 'fallback-subscription' && subscriptionState.currentSubscription.id !== 'error-fallback-subscription') {
      setModalCheckCompleted(false);
    }
  }, [subscriptionState.currentSubscription?.id]);

  // Check subscription and update user state if needed, then show modal
  useEffect(() => {
    if (!user?.id || user.skippedTrialOffer || modalCheckCompleted) return;

    let timer: NodeJS.Timeout | null = null;

    const checkAndShowModal = async () => {
      // Wait for subscription to finish loading
      if (subscriptionState.isLoading) {
        console.log('⏳ DashboardLayout: Waiting for subscription to load...');
        return;
      }

      // If subscription is loaded and user is in trial
      if (subscriptionState.currentSubscription) {
        const subscription = subscriptionState.currentSubscription;
        
        // Skip fallback subscriptions - wait for real subscription
        if (subscription.id === 'fallback-subscription' || subscription.id === 'error-fallback-subscription') {
          console.log('⏳ DashboardLayout: Subscription is fallback, waiting for real subscription...');
          return;
        }

        console.log('✅ DashboardLayout: Subscription loaded:', {
          id: subscription.id,
          planId: subscription.planId,
          status: subscription.status,
          endDate: subscription.endDate
        });

        const now = Date.now();
        const isTrialActive = subscription.status === 'trial' &&
          subscription.planId === SUBSCRIPTION_PLAN_IDS.PROFESSIONAL &&
          subscription.endDate &&
          now < parseInt(subscription.endDate);

        if (isTrialActive) {
          console.log('🎉 DashboardLayout: User is in active trial, showing modal');
          // Update user state if shouldShowTrialModal is not set
          if (!user.shouldShowTrialModal) {
            const currentUser = auth.currentUser;
            if (currentUser) {
              const updatedUserData = {
                ...user,
                shouldShowTrialModal: true
              };
              // Update Firestore
              await setDoc(doc(db, 'Users', currentUser.uid), updatedUserData, { merge: true });
              // Update context
              setUser(updatedUserData);
              // Wait a bit for state to update, then show modal
              timer = setTimeout(() => {
                setShowFreeTrialModal(true);
                setModalCheckCompleted(true);
              }, 300);
            } else {
              // If user is not authenticated, just show modal
              timer = setTimeout(() => {
                setShowFreeTrialModal(true);
                setModalCheckCompleted(true);
              }, 500);
            }
          } else {
            // User already has shouldShowTrialModal set, show modal
            timer = setTimeout(() => {
              setShowFreeTrialModal(true);
              setModalCheckCompleted(true);
            }, 500);
          }
        } else {
          // Subscription is loaded but not in trial, mark check as completed
          console.log('ℹ️ DashboardLayout: Subscription loaded but not in trial');
          setModalCheckCompleted(true);
        }
      }
      // Also check if user already has shouldShowTrialModal set (from AuthContext)
      // This handles the case where subscription hasn't loaded yet but AuthContext already set the flag
      else if (user.shouldShowTrialModal) {
        console.log('🎉 DashboardLayout: User has shouldShowTrialModal flag, showing modal');
        timer = setTimeout(() => {
          setShowFreeTrialModal(true);
          setModalCheckCompleted(true);
        }, 500);
      } else if (!subscriptionState.isLoading) {
        // Subscription finished loading but no subscription found, mark check as completed
        console.log('ℹ️ DashboardLayout: Subscription finished loading but no subscription found');
        setModalCheckCompleted(true);
      }
    };

    checkAndShowModal();

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [
    user?.id,
    user?.shouldShowTrialModal,
    user?.skippedTrialOffer,
    subscriptionState.currentSubscription?.id,
    subscriptionState.currentSubscription?.status,
    subscriptionState.currentSubscription?.planId,
    subscriptionState.isLoading,
    modalCheckCompleted,
    setUser
  ]);

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      // Still navigate to login even if logout fails
      navigate('/login');
    }
  };

  // Handle trial acceptance
  const handleAcceptTrial = async () => {
    if (!user?.id) return;
    
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      // Update user data to mark that they accepted the trial
      const updatedUserData = {
        ...user,
        skippedTrialOffer: false,
        shouldShowTrialModal: false
      };

      // Update user document in Firestore
      await setDoc(doc(db, 'Users', currentUser.uid), updatedUserData, { merge: true });

      // Update user state in context
      setUser(updatedUserData);
      setShowFreeTrialModal(false);
    } catch (error) {
      console.error('Error accepting trial:', error);
      setShowFreeTrialModal(false);
    }
  };

  // Handle skip trial
  const handleSkipTrial = async () => {
    if (!user?.id) return;
    
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      // Update user data to mark that they skipped the trial
      const updatedUserData = {
        ...user,
        skippedTrialOffer: true,
        shouldShowTrialModal: false
      };

      // Update user document in Firestore
      await setDoc(doc(db, 'Users', currentUser.uid), updatedUserData, { merge: true });

      // Update user state in context
      setUser(updatedUserData);
      setShowFreeTrialModal(false);
    } catch (error) {
      console.error('Error skipping trial:', error);
      setShowFreeTrialModal(false);
    }
  };

  // Handle trial modal error
  const handleTrialError = (error: string) => {
    console.error('Trial modal error:', error);
    setShowFreeTrialModal(false);
    // Update user state to prevent modal from showing again
    if (user) {
      const updatedUserData = {
        ...user,
        shouldShowTrialModal: false
      };
      setUser(updatedUserData);
    }
  };


  return (
    <div className="min-h-screen bg-background-light dark:bg-background-dark text-black dark:text-grey-light transition-all duration-500 flex">
      {/* Sidebar Component */}
      <Sidebar />

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Navigation - Material Design 3 Style */}
        <header className="m3-surface m3-elevation-2 backdrop-blur-xl border-b border-grey-light dark:border-grey transition-all duration-500 bg-white dark:bg-primary-dark">
          <div className="px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="m3-headline-medium text-primary dark:text-white">
                  {getWelcomeMessage()}
                </h2>
                <p className="m3-body-large text-grey-dark dark:text-grey mt-1">
                  {getUserTitle()}
                </p>
              </div>
              <div className="flex items-center space-x-2">
                  {/* Theme Toggle Button */}
                  <button
                    onClick={toggleTheme}
                    className="px-4 py-2 rounded-2xl text-sm font-medium transition-all duration-300 m3-elevation-1 hover:m3-elevation-2 m3-ripple bg-grey-light text-grey-dark dark:bg-grey dark:text-grey-light hover:bg-grey dark:hover:bg-primary-light"
                    title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                  >
                    {isDark ? '☀️' : '🌙'}
                  </button>
                  
                  {/* Language Switcher Toggle */}
                  <button
                    onClick={() => setLanguage(language === 'ar' ? 'en' : 'ar')}
                    className="px-4 py-2 rounded-2xl text-sm font-medium transition-all duration-300 m3-elevation-1 hover:m3-elevation-2 m3-ripple bg-primary text-white dark:bg-primary-light dark:text-primary-dark"
                  >
                    {language === 'ar' ? 'English' : 'العربية'}
                  </button>
                  
                  {/* Logout Button */}
                  <button
                    onClick={handleLogout}
                    className="px-4 py-2 rounded-2xl text-sm font-medium transition-all duration-300 m3-elevation-1 hover:m3-elevation-2 m3-ripple bg-red-500 text-white hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700 flex items-center space-x-2"
                    title={language === 'ar' ? 'تسجيل الخروج' : 'Logout'}
                  >
                    <LogOut size={16} />
                    <span>{language === 'ar' ? 'تسجيل الخروج' : 'Logout'}</span>
                  </button>
                </div>
              </div>
            </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-hidden">
          <Outlet />
        </main>
      </div>

      {/* Free Trial Modal - Show after successful navigation to dashboard */}
      <FreeTrialModal
        isOpen={showFreeTrialModal}
        onAccept={handleAcceptTrial}
        onError={handleTrialError}
        onSkip={handleSkipTrial}
      />
    </div>
  );
};

export default DashboardLayout; 